// Horizontal progress rail for the transaction-status popup.
//
// One node per stage the flow ACTUALLY has a signal for — never a node that
// would sit on "active" forever because nothing can ever complete it. See
// `docs/track-progress-redesign-plan.md` §2 for the per-flow node tables.
//
// Colour comes exclusively from `useTheme()` so the rail renders in both dark
// and light; there is no literal hex in this file by design.
import React, { FC, useEffect, useMemo, useRef } from 'react'
import { Animated, ColorValue, Easing, View } from 'react-native'

import CheckIcon from '@common/assets/svg/CheckIcon'
import CloseIcon from '@common/assets/svg/CloseIcon'
import WarningIcon from '@common/assets/svg/WarningIcon'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { ThemeProps } from '@common/styles/themeConfig'
import flexbox from '@common/styles/utils/flexbox'
import text from '@common/styles/utils/text'

export type RailState = 'done' | 'active' | 'pending' | 'failed' | 'refunded'

export type RailStep = {
  /** Already translated — the rail never calls `t()` itself. */
  label: string
  /**
   * Label while the stage is `active`. A stage in flight is not the stage
   * finished: a node reading "Confirmed" while it is still confirming says the
   * opposite of what the rail is showing. Falls back to `label` when a flow has
   * no sensible in-flight wording.
   */
  activeLabel?: string
  state: RailState
}

export const NODE_SIZE = 28

/**
 * The line LEADING OUT of a node takes that node's colour, so a rail reads as a
 * filled track up to wherever the flow currently is.
 */
const getConnectorColor = (state: RailState, theme: ThemeProps): ColorValue => {
  if (state === 'done') return theme.successDecorative
  if (state === 'active') return theme.primary
  if (state === 'failed') return theme.errorDecorative
  if (state === 'refunded') return theme.warningDecorative

  return theme.primaryBorder
}

/**
 * Expanding ring behind the node in flight. Without it the rail is completely
 * static while waiting, which reads as a frozen screen — the very thing a
 * progress rail exists to disprove. It lives here rather than at a call site so
 * every flow gets it; each one has exactly one node in flight at a time.
 *
 * `useNativeDriver` is false on purpose: the web targets have no native animated
 * module, and asking for one there only logs a fallback warning.
 */
const PulseRing: FC<{ color: ColorValue }> = ({ color }) => {
  const progress = useRef(new Animated.Value(0)).current

  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(progress, {
        toValue: 1,
        duration: 1600,
        easing: Easing.out(Easing.ease),
        useNativeDriver: false
      })
    )
    loop.start()

    return () => loop.stop()
  }, [progress])

  return (
    <Animated.View
      pointerEvents="none"
      style={{
        position: 'absolute',
        width: NODE_SIZE,
        height: NODE_SIZE,
        borderRadius: NODE_SIZE / 2,
        borderWidth: 1,
        borderColor: color,
        opacity: progress.interpolate({ inputRange: [0, 1], outputRange: [0.55, 0] }),
        transform: [{ scale: progress.interpolate({ inputRange: [0, 1], outputRange: [1, 1.9] }) }]
      }}
    />
  )
}

const Node: FC<{ state: RailState }> = ({ state }) => {
  const { theme } = useTheme()

  const { backgroundColor, borderColor } = useMemo(() => {
    if (state === 'done')
      return { backgroundColor: theme.successBackground, borderColor: 'transparent' as ColorValue }
    if (state === 'active')
      // The active tint is nearly the card colour in light mode, so the node
      // would read as a bare dot with no step marker around it. The ring is
      // what makes it read as "the step we are on".
      return { backgroundColor: theme.tertiaryBackground, borderColor: theme.primary }
    if (state === 'failed')
      return { backgroundColor: theme.errorBackground, borderColor: 'transparent' as ColorValue }
    if (state === 'refunded')
      return { backgroundColor: theme.warningBackground, borderColor: 'transparent' as ColorValue }

    return { backgroundColor: theme.quaternaryBackground, borderColor: theme.primaryBorder }
  }, [state, theme])

  return (
    <View
      style={[
        flexbox.center,
        {
          width: NODE_SIZE,
          height: NODE_SIZE,
          borderRadius: NODE_SIZE / 2,
          borderWidth: 1,
          backgroundColor,
          borderColor
        }
      ]}
    >
      {state === 'done' && (
        // CheckIcon draws its own pill background; the node already provides
        // one, so knock it out and keep only the glyph.
        <CheckIcon
          width={16}
          height={16}
          color="transparent"
          checkColor={theme.successDecorative}
        />
      )}
      {/* Plan §5, rule 3, settled by looking at both themes. A `Spinner` was
          the obvious pick, but it is a fixed-colour Lottie AND at 16px inside a
          28px node any single frame of it is a hairline arc — it read as an
          artifact on the light `tertiaryBackground` (#E8F0FF) and barely
          better on the dark one. A solid dot is unambiguous at this size, and
          `primary` expresses it in both themes with no theme branch at all.
          "In progress" is carried by the colour, the trailing connector and the
          bolder label, not by motion this small. */}
      {state === 'active' && (
        <>
          <PulseRing color={theme.primary} />
          <View
            style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: theme.primary }}
          />
        </>
      )}
      {state === 'failed' && (
        <CloseIcon width={12} height={12} color={theme.errorDecorative} strokeWidth="2" />
      )}
      {state === 'refunded' && (
        <WarningIcon width={15} height={15} color={theme.warningDecorative} />
      )}
      {state === 'pending' && (
        <View
          style={{
            width: 6,
            height: 6,
            borderRadius: 3,
            backgroundColor: theme.iconPrimary
          }}
        />
      )}
    </View>
  )
}

type Props = {
  steps: RailStep[]
}

const StatusRail: FC<Props> = ({ steps }) => {
  const { theme } = useTheme()

  if (!steps.length) return null

  return (
    <View style={[flexbox.directionRow, flexbox.alignStart, { width: '100%' }]}>
      {steps.map((step, index) => {
        const isFirst = index === 0
        const isLast = index === steps.length - 1
        const previous = steps[index - 1]

        const labelColor = (() => {
          if (step.state === 'failed') return theme.errorDecorative
          if (step.state === 'refunded') return theme.warningDecorative
          if (step.state === 'active' || step.state === 'done') return theme.primaryText

          return theme.secondaryText
        })()

        return (
          // Each stage owns an equal-width column, and the connector is split
          // into the two half-segments that flank the node. That is what keeps
          // a label centred under its own node no matter how many stages the
          // flow has — a single full-width connector row cannot do that.
          <View
            // eslint-disable-next-line react/no-array-index-key
            key={`${step.label}-${index}`}
            style={[flexbox.alignCenter, { flex: 1, minWidth: 0 }]}
          >
            <View style={[flexbox.directionRow, flexbox.alignCenter, { width: '100%' }]}>
              <View
                style={{
                  flex: 1,
                  height: 2,
                  backgroundColor:
                    isFirst || !previous ? 'transparent' : getConnectorColor(previous.state, theme)
                }}
              />
              <Node state={step.state} />
              <View
                style={{
                  flex: 1,
                  height: 2,
                  backgroundColor: isLast ? 'transparent' : getConnectorColor(step.state, theme)
                }}
              />
            </View>
            <Text
              fontSize={12}
              weight={step.state === 'active' ? 'medium' : 'regular'}
              color={labelColor}
              numberOfLines={2}
              style={[text.center, { marginTop: 8, paddingHorizontal: 2 }]}
            >
              {step.state === 'active' ? step.activeLabel || step.label : step.label}
            </Text>
          </View>
        )
      })}
    </View>
  )
}

export default React.memo(StatusRail)
